import { useEffect, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

const CATEGORY_LABELS:Record<string,string>={
  PROFILE:"학생 기본정보",
  CAREER_SUMMARY:"진로목표·지원계획 요약",
  ASSESSMENT_SUMMARY:"검사 실시 요약",
  ACTIVITY_SUMMARY:"수업·활동 참여 요약",
  EVIDENCE_SUMMARY:"검증된 결과물 요약",
  CREDENTIAL_SUMMARY:"디지털배지 요약"
};

function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function statusLabel(value:string){
  const map:Record<string,string>={
    PENDING:"동의 확인 대기",
    GRANTED:"동의함",
    DENIED:"동의하지 않음",
    WITHDRAWN:"동의 철회",
    EXPIRED:"만료",
    CANCELLED:"요청 취소",
    ACTIVE:"공유 가능"
  };
  return map[value] || value;
}

type ConsentView={
  consentRequestId:string;
  status:string;
  schoolName:string;
  learnerName:string;
  guardianName:string;
  guardianEmail:string;
  relationshipType:string;
  purpose:{title:string;description:string;purposeKey:string;purposeVersion:number};
  recipient:{type:string;name:string};
  dataCategories:string[];
  requestExpiresAt:string;
  requestedValidDays:number;
  consentValidUntil?:string|null;
  decisionSignerName?:string|null;
  decidedAt?:string|null;
  withdrawnAt?:string|null;
};

export function GuardianConsentPublicPage({
  client,token
}:{
  client:PlatformClient;
  token:string;
}){
  const [data,setData]=useState<ConsentView|null>(null);
  const [signer,setSigner]=useState("");
  const [note,setNote]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    if(!token){setError("동의 링크가 올바르지 않습니다.");return;}
    client.rpc("public_guardian_consent_request",{p_token:token}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as ConsentView;
      setData(next);
      setSigner((current)=>current || next.guardianName || "");
    });
    return()=>{active=false;};
  },[client,token,refresh]);

  async function decide(decision:"GRANT"|"DENY"){
    if(!signer.trim())return;
    setBusy(true);setError("");setMessage("");
    const {error}=await client.rpc("public_decide_guardian_consent",{
      p_token:token,p_decision:decision,p_signer_name:signer.trim(),
      p_note:note.trim() || null
    });
    setBusy(false);
    if(error){setError(error.message);return;}
    setMessage(decision==="GRANT"?"동의가 기록되었습니다.":"미동의가 기록되었습니다.");
    setRefresh((value)=>value+1);
  }

  async function withdraw(){
    if(!signer.trim())return;
    setBusy(true);setError("");setMessage("");
    const {error}=await client.rpc("public_withdraw_guardian_consent",{
      p_token:token,p_signer_name:signer.trim(),p_note:note.trim() || null
    });
    setBusy(false);
    if(error){setError(error.message);return;}
    setMessage("동의 철회가 기록되었고 연결된 활성 공유도 종료되었습니다.");
    setRefresh((value)=>value+1);
  }

  return (
    <main className="erp12-public-page">
      <section className="erp12-public-card">
        <div className="erp12-public-brand"><span className="brand-mark">RS</span><div><strong>RS CAREER ERP</strong><small>보호자 동의 확인</small></div></div>
        {error ? (
          <div className="erp12-public-state error"><strong>링크를 확인할 수 없습니다.</strong><p>{error}</p></div>
        ) : !data ? (
          <p className="muted">동의 내용을 불러오는 중…</p>
        ) : (
          <>
            <header>
              <p className="eyebrow">Guardian Consent</p>
              <h1>{data.purpose.title}</h1>
              <p>{data.purpose.description}</p>
            </header>

            {message && <p className="teacher-success">{message}</p>}

            <div className="erp12-public-summary">
              <div><span>학생</span><strong>{data.learnerName}</strong></div>
              <div><span>학교</span><strong>{data.schoolName}</strong></div>
              <div><span>보호자</span><strong>{data.guardianName} · {data.guardianEmail}</strong></div>
              <div><span>수신자</span><strong>{data.recipient.name}</strong></div>
              <div><span>동의 기간</span><strong>최대 {data.requestedValidDays}일</strong></div>
              <div><span>현재 상태</span><strong>{statusLabel(data.status)}</strong></div>
            </div>

            <section className="erp12-public-section">
              <h2>공유하는 정보</h2>
              <div className="erp12-public-categories">
                {data.dataCategories.map((item)=><span key={item}>{CATEGORY_LABELS[item] || item}</span>)}
              </div>
            </section>

            <section className="erp12-public-guardrail">
              <strong>이 동의로 공유하지 않는 정보</strong>
              <p>심리검사 원점수·백분위·해석 원문, 상담 세부 대화와 노트, 증빙 원본 파일, 교사 Case 공유노트는 포함되지 않습니다.</p>
            </section>

            {data.status==="PENDING" && (
              <section className="erp12-public-decision">
                <label>동의 확인자 이름<input value={signer} onChange={(e)=>setSigner(e.target.value)} /></label>
                <label>메모 (선택)<textarea rows={2} value={note} onChange={(e)=>setNote(e.target.value)} /></label>
                <small>이 요청 링크는 {fmt(data.requestExpiresAt)}까지 사용할 수 있습니다.</small>
                <div>
                  <button className="primary" disabled={busy || !signer.trim()} onClick={()=>decide("GRANT")}>위 내용에 동의합니다</button>
                  <button className="secondary" disabled={busy || !signer.trim()} onClick={()=>decide("DENY")}>동의하지 않습니다</button>
                </div>
              </section>
            )}

            {data.status==="GRANTED" && (
              <section className="erp12-public-decision">
                <div className="erp12-public-state good">
                  <strong>동의가 기록되어 있습니다.</strong>
                  <p>유효기간: {fmt(data.consentValidUntil)}</p>
                </div>
                <label>철회 확인자 이름<input value={signer} onChange={(e)=>setSigner(e.target.value)} /></label>
                <label>철회 메모 (선택)<textarea rows={2} value={note} onChange={(e)=>setNote(e.target.value)} /></label>
                <button className="secondary danger" disabled={busy || !signer.trim()} onClick={withdraw}>동의 철회</button>
              </section>
            )}

            {["DENIED","WITHDRAWN","EXPIRED","CANCELLED"].includes(data.status) && (
              <div className="erp12-public-state">
                <strong>{statusLabel(data.status)}</strong>
                <p>이 링크에서는 새로운 데이터 공유를 승인할 수 없습니다.</p>
              </div>
            )}

            <footer>
              <span>Purpose {data.purpose.purposeKey} · v{data.purpose.purposeVersion}</span>
              {data.decidedAt && <span>결정 {fmt(data.decidedAt)}</span>}
            </footer>
          </>
        )}
      </section>
    </main>
  );
}

type DataShareView={
  shareId:string;
  status:string;
  purpose:{title:string;description?:string;purposeKey:string;purposeVersion:number};
  recipient:{type:string;name:string};
  dataCategories:string[];
  projectionVersion:string;
  snapshotHash:string;
  expiresAt:string;
  data:{
    projectionVersion:string;
    generatedAt:string;
    categories:string[];
    privacy:{
      rawAssessmentScoresIncluded:boolean;
      counselingNotesIncluded:boolean;
      evidenceFilesIncluded:boolean;
      caseNotesIncluded:boolean;
    };
    profile?:{
      learnerName:string;
      schoolName:string;
      academicYear?:string|null;
      gradeLevel?:string|null;
      classLabel?:string|null;
    };
    careerSummary?:{
      goals:Array<{title:string;goalType:string;status:string;targetOn?:string|null}>;
      plans:Array<{title:string;planType:string;status:string;plannedFrom?:string|null;plannedUntil?:string|null}>;
    };
    assessmentSummary?:Array<{title:string;status:string;completedAt?:string|null}>;
    activitySummary?:Array<{title:string;activityType:string;status:string;attendedAt?:string|null;completedAt?:string|null}>;
    evidenceSummary?:Array<{title:string;evidenceType:string;verifiedAt?:string|null}>;
    credentialSummary?:Array<{name:string;status:string;issuedAt?:string|null;verificationUri?:string|null}>;
  };
};

export function StudentDataSharePublicPage({
  client,token
}:{
  client:PlatformClient;
  token:string;
}){
  const [data,setData]=useState<DataShareView|null>(null);
  const [error,setError]=useState("");

  useEffect(()=>{
    let active=true;
    setError("");
    if(!token){setError("공유 링크가 올바르지 않습니다.");return;}
    client.rpc("public_student_data_share",{p_token:token}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setData(data as DataShareView);
    });
    return()=>{active=false;};
  },[client,token]);

  return (
    <main className="erp12-public-page">
      <section className="erp12-public-card wide">
        <div className="erp12-public-brand"><span className="brand-mark">RS</span><div><strong>RS CAREER ERP</strong><small>동의 기반 학생정보 공유</small></div></div>
        {error ? (
          <div className="erp12-public-state error"><strong>공유를 열 수 없습니다.</strong><p>{error}</p></div>
        ) : !data ? (
          <p className="muted">공유 정보를 불러오는 중…</p>
        ) : (
          <>
            <header>
              <p className="eyebrow">Consent-bound Minimal Projection</p>
              <h1>{data.purpose.title}</h1>
              <p>{data.purpose.description || "동의된 범위의 최소정보만 제공합니다."}</p>
            </header>

            <div className="erp12-share-meta">
              <div><span>수신자</span><strong>{data.recipient.name}</strong></div>
              <div><span>만료</span><strong>{fmt(data.expiresAt)}</strong></div>
              <div><span>Projection</span><strong>{data.projectionVersion}</strong></div>
              <div><span>Snapshot</span><code>{data.snapshotHash.slice(0,24)}…</code></div>
            </div>

            <section className="erp12-public-guardrail good">
              <strong>최소정보 공유가 적용되어 있습니다.</strong>
              <p>검사 원점수, 상담노트, Evidence 원본파일, Case 노트는 이 화면에 포함되지 않습니다.</p>
            </section>

            {data.data.profile && (
              <section className="erp12-data-section">
                <h2>학생 기본정보</h2>
                <div className="erp12-data-profile">
                  <div><span>학생</span><strong>{data.data.profile.learnerName}</strong></div>
                  <div><span>학교</span><strong>{data.data.profile.schoolName}</strong></div>
                  <div><span>학년도</span><strong>{data.data.profile.academicYear || "-"}</strong></div>
                  <div><span>학년·반</span><strong>{data.data.profile.gradeLevel?data.data.profile.gradeLevel+"학년":"-"}{data.data.profile.classLabel?" · "+data.data.profile.classLabel+"반":""}</strong></div>
                </div>
              </section>
            )}

            {data.data.careerSummary && (
              <section className="erp12-data-section">
                <h2>진로목표·지원계획</h2>
                <div className="erp12-public-list">
                  {data.data.careerSummary.goals.map((item,index)=><article key={"g-"+index}><strong>{item.title}</strong><span>{item.goalType+" · "+item.status+" · 목표 "+(item.targetOn || "-")}</span></article>)}
                  {data.data.careerSummary.plans.map((item,index)=><article key={"p-"+index}><strong>{item.title}</strong><span>{item.planType+" · "+item.status+" · "+(item.plannedFrom || "-")+" ~ "+(item.plannedUntil || "-")}</span></article>)}
                  {!data.data.careerSummary.goals.length && !data.data.careerSummary.plans.length && <p className="case-empty">공유할 진로목표·지원계획 요약이 없습니다.</p>}
                </div>
              </section>
            )}

            {data.data.assessmentSummary && (
              <SummaryList title="검사 실시 요약" items={data.data.assessmentSummary.map((item)=>({title:item.title,meta:item.status+" · 완료 "+fmt(item.completedAt)}))}/>
            )}
            {data.data.activitySummary && (
              <SummaryList title="수업·활동 참여 요약" items={data.data.activitySummary.map((item)=>({title:item.title,meta:item.activityType+" · "+item.status+" · "+fmt(item.completedAt || item.attendedAt)}))}/>
            )}
            {data.data.evidenceSummary && (
              <SummaryList title="검증된 결과물 요약" items={data.data.evidenceSummary.map((item)=>({title:item.title,meta:item.evidenceType+" · 검증 "+fmt(item.verifiedAt)}))}/>
            )}
            {data.data.credentialSummary && (
              <section className="erp12-data-section">
                <h2>디지털배지 요약</h2>
                <div className="erp12-public-list">
                  {data.data.credentialSummary.map((item,index)=><article key={index}><strong>{item.name}</strong><span>{item.status+" · 발급 "+fmt(item.issuedAt)}</span>{item.verificationUri && <a href={item.verificationUri} target="_blank" rel="noreferrer">검증 링크</a>}</article>)}
                  {!data.data.credentialSummary.length && <p className="case-empty">공유할 디지털배지 요약이 없습니다.</p>}
                </div>
              </section>
            )}

            <footer>
              <span>{data.dataCategories.map((item)=>CATEGORY_LABELS[item] || item).join(" · ")}</span>
              <span>Snapshot 생성 {fmt(data.data.generatedAt)}</span>
            </footer>
          </>
        )}
      </section>
    </main>
  );
}

function SummaryList({title,items}:{title:string;items:Array<{title:string;meta:string}>}){
  return (
    <section className="erp12-data-section">
      <h2>{title}</h2>
      <div className="erp12-public-list">
        {items.map((item,index)=><article key={index}><strong>{item.title}</strong><span>{item.meta}</span></article>)}
        {!items.length && <p className="case-empty">공유할 요약이 없습니다.</p>}
      </div>
    </section>
  );
}
